(() => {
  if (customElements.get('abok-backup-calculator')) return;

  class AbokBackupCalculator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };

      this.applianceButtons = [...this.querySelectorAll('[data-appliance]')];
      this.appliances = this.querySelector('.abok-backup-calculator__appliances');
      this.applianceDrag = null;
      this.suppressApplianceClick = false;
      this.manualInput = this.querySelector('[data-manual-load]');
      this.stationSelect = this.querySelector('[data-station-select]');
      this.stationDropdown = this.querySelector('[data-station-dropdown]');
      this.stationTrigger = this.querySelector('[data-station-trigger]');
      this.stationOptionList = this.querySelector('[data-station-options]');
      this.stationOptions = [...this.querySelectorAll('[data-station-option]')];
      this.addonPanel = this.querySelector('[data-addon-panel]');
      this.addonCount = 0;
      this.addonMax = 0;
      this.addonCapacity = 0;

      this.applianceButtons.forEach((button) => {
        button.addEventListener('click', () => this.toggleAppliance(button), options);
      });
      this.appliances?.addEventListener('pointerdown', (event) => this.startApplianceDrag(event), options);
      this.appliances?.addEventListener('click', (event) => this.onApplianceClick(event), {
        capture: true,
        signal: this.abortController.signal,
      });
      this.appliances?.addEventListener('dragstart', (event) => event.preventDefault(), options);
      document.addEventListener('pointermove', (event) => this.moveApplianceDrag(event), options);
      document.addEventListener('pointerup', (event) => this.endApplianceDrag(event), options);
      document.addEventListener('pointercancel', (event) => this.endApplianceDrag(event), options);
      this.manualInput?.addEventListener('input', () => this.useManualLoad(), options);
      this.stationSelect?.addEventListener('change', () => this.updateStation(), options);
      this.stationTrigger?.addEventListener('click', () => this.toggleStationDropdown(), options);
      this.stationTrigger?.addEventListener('keydown', (event) => this.onStationTriggerKeydown(event), options);
      this.stationOptions.forEach((option, index) => {
        option.addEventListener('click', () => this.selectStationOption(option), options);
        option.addEventListener('keydown', (event) => this.onStationOptionKeydown(event, index), options);
      });
      this.querySelectorAll('[data-addon-step]').forEach((button) => {
        button.addEventListener('click', () => this.stepAddon(Number(button.dataset.addonStep)), options);
      });
      document.addEventListener('click', (event) => this.onDocumentClick(event), options);
      this.closest('modal-element')?.addEventListener('modal:beforeHide', () => this.closeStationDropdown(), options);

      this.syncInputFromAppliances();
      this.updateStation();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.initialized = false;
    }

    get selectedStation() {
      return this.stationSelect?.selectedOptions?.[0] || null;
    }

    startApplianceDrag(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !this.appliances) return;
      if (this.appliances.scrollWidth <= this.appliances.clientWidth) return;

      this.applianceDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        scrollLeft: this.appliances.scrollLeft,
        dragging: false,
      };
    }

    moveApplianceDrag(event) {
      if (!this.applianceDrag || event.pointerId !== this.applianceDrag.pointerId || !this.appliances) return;
      if ((event.buttons & 1) === 0) {
        this.endApplianceDrag(event);
        return;
      }

      const distance = event.clientX - this.applianceDrag.startX;
      if (!this.applianceDrag.dragging && Math.abs(distance) < 6) return;

      event.preventDefault();
      this.applianceDrag.dragging = true;
      this.appliances.classList.add('is-dragging');
      this.appliances.scrollLeft = this.applianceDrag.scrollLeft - distance;
    }

    endApplianceDrag(event) {
      if (!this.applianceDrag || event.pointerId !== this.applianceDrag.pointerId) return;

      const wasDragging = this.applianceDrag.dragging;
      this.applianceDrag = null;
      this.appliances?.classList.remove('is-dragging');
      if (!wasDragging) return;

      this.suppressApplianceClick = true;
      window.setTimeout(() => {
        this.suppressApplianceClick = false;
      }, 0);
    }

    onApplianceClick(event) {
      if (!this.suppressApplianceClick) return;

      event.preventDefault();
      event.stopPropagation();
      this.suppressApplianceClick = false;
    }

    toggleStationDropdown() {
      if (this.stationTrigger?.getAttribute('aria-expanded') === 'true') {
        this.closeStationDropdown();
        return;
      }

      this.openStationDropdown();
    }

    openStationDropdown(focusIndex = null) {
      if (!this.stationTrigger || !this.stationOptionList || this.stationOptions.length === 0) return;

      this.stationOptionList.hidden = false;
      this.stationDropdown?.classList.add('is-open');
      this.stationTrigger.setAttribute('aria-expanded', 'true');

      if (focusIndex === null) return;

      const availableOptions = this.stationOptions.filter((option) => !option.disabled);
      const target = focusIndex === -1
        ? availableOptions.at(-1)
        : this.stationOptions[focusIndex] || availableOptions[0];
      requestAnimationFrame(() => target?.focus());
    }

    closeStationDropdown(restoreFocus = false) {
      if (!this.stationTrigger || !this.stationOptionList) return;

      this.stationOptionList.hidden = true;
      this.stationDropdown?.classList.remove('is-open');
      this.stationTrigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.stationTrigger.focus();
    }

    selectStationOption(option) {
      if (!this.stationSelect || option.disabled) return;

      this.stationSelect.value = option.dataset.value || '';
      this.updateStation();
      this.closeStationDropdown(true);
    }

    onDocumentClick(event) {
      if (!(event.target instanceof Node) || this.stationDropdown?.contains(event.target)) return;
      this.closeStationDropdown();
    }

    onStationTriggerKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const selectedIndex = this.stationOptions.findIndex((option) => option.getAttribute('aria-selected') === 'true');
        this.openStationDropdown(event.key === 'ArrowUp' ? -1 : Math.max(0, selectedIndex));
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeStationDropdown();
      }
    }

    onStationOptionKeydown(event, index) {
      const lastIndex = this.stationOptions.length - 1;
      let targetIndex = null;

      if (event.key === 'ArrowDown') targetIndex = index === lastIndex ? 0 : index + 1;
      if (event.key === 'ArrowUp') targetIndex = index === 0 ? lastIndex : index - 1;
      if (event.key === 'Home') targetIndex = 0;
      if (event.key === 'End') targetIndex = lastIndex;

      if (targetIndex !== null) {
        event.preventDefault();
        this.stationOptions[targetIndex]?.focus();
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectStationOption(this.stationOptions[index]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closeStationDropdown(true);
      } else if (event.key === 'Tab') {
        this.closeStationDropdown();
      }
    }

    toggleAppliance(button) {
      const selected = button.getAttribute('aria-pressed') === 'true';
      button.setAttribute('aria-pressed', String(!selected));
      button.classList.toggle('is-selected', !selected);
      this.syncInputFromAppliances();
      this.updateResult();
    }

    syncInputFromAppliances() {
      if (!this.manualInput) return;
      this.manualInput.value = String(this.getApplianceLoad());
    }

    useManualLoad() {
      this.applianceButtons.forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.classList.remove('is-selected');
      });
      this.updateResult();
    }

    updateStation() {
      const option = this.selectedStation;
      const title = option?.dataset.title || '';
      const image = option?.dataset.image || '';
      const capacity = this.toNumber(option?.dataset.capacity);
      const output = this.toNumber(option?.dataset.output);
      const stationImage = this.querySelector('[data-station-image]');
      const addonImage = this.querySelector('img[data-addon-image]');

      this.setText('[data-station-trigger-label]', title);
      this.stationOptions.forEach((stationOption) => {
        const selected = stationOption.dataset.value === option?.value;
        stationOption.classList.toggle('is-selected', selected);
        stationOption.setAttribute('aria-selected', String(selected));
      });
      this.setText('[data-station-title]', title);
      this.setText('[data-station-capacity]', capacity > 0 ? this.formatNumber(capacity, 0) : '--');
      this.setText('[data-station-output]', output > 0 ? this.formatNumber(output, 0) : '--');

      if (stationImage) {
        stationImage.hidden = !image;
        if (image) {
          stationImage.src = image;
          stationImage.alt = option?.dataset.imageAlt || title;
        } else {
          stationImage.removeAttribute('src');
          stationImage.alt = '';
        }
      }

      if (addonImage) {
        const image = option?.dataset.addonImage || '';
        addonImage.hidden = !image;
        if (image) {
          addonImage.src = image;
          addonImage.alt = option?.dataset.addonImageAlt || option?.dataset.addonTitle || '';
        } else {
          addonImage.removeAttribute('src');
          addonImage.alt = '';
        }
      }

      this.addonCount = 0;
      this.addonCapacity = this.toNumber(option?.dataset.addonCapacity);
      this.addonMax = Math.max(0, Math.floor(this.toNumber(option?.dataset.addonMax)));
      const addonAvailable = this.addonCapacity > 0 && this.addonMax > 0;
      if (this.addonPanel) this.addonPanel.hidden = !addonAvailable;
      this.setText('.abok-backup-calculator__addon [data-addon-title]', option?.dataset.addonTitle || '');
      this.setText('.abok-backup-calculator__addon [data-addon-capacity]', this.formatNumber(this.addonCapacity, 0));
      this.setText('[data-addon-count]', '0');
      this.updateResult();
    }

    stepAddon(step) {
      const nextCount = Math.max(0, this.addonCount + step);
      if (nextCount > this.addonMax) {
        const message = (this.dataset.maximumText || '').replace('{count}', String(this.addonMax));
        window.alert(message);
        return;
      }

      this.addonCount = nextCount;
      this.setText('[data-addon-count]', String(this.addonCount));
      this.updateResult();
    }

    getLoad() {
      return Math.max(0, this.toNumber(this.manualInput?.value));
    }

    getApplianceLoad() {
      return this.applianceButtons.reduce((total, button) => {
        if (button.getAttribute('aria-pressed') !== 'true') return total;
        return total + this.toNumber(button.dataset.watts);
      }, 0);
    }

    updateResult() {
      const option = this.selectedStation;
      const capacity = this.toNumber(option?.dataset.capacity);
      const output = this.toNumber(option?.dataset.output);
      const totalCapacity = capacity + this.addonCapacity * this.addonCount;
      const load = this.getLoad();

      this.setText('[data-load-total]', this.formatNumber(load, 0));
      this.setText('[data-total-capacity]', totalCapacity > 0 ? this.formatNumber(totalCapacity, 0) : '--');
      this.setText('[data-output-limit]', output > 0 ? this.formatNumber(output, 0) : '--');

      if (capacity <= 0 || output <= 0) {
        this.setResult(this.dataset.unavailableText, 'unavailable');
        return;
      }

      if (load <= 0) {
        this.setResult(this.dataset.emptyText, 'empty');
        return;
      }

      if (load > output) {
        console.log(this.dataset.overloadText)
        this.setResult(this.dataset.overloadText, 'error');
        return;
      }

      const runtime = totalCapacity / load;
      this.setResult(`${this.formatNumber(runtime, 2)} ${this.dataset.hoursText || 'hours'}`, 'ready');
    }

    setResult(text, state) {
      const result = this.querySelector('[data-result]');
      const value = this.querySelector('[data-result-value]');
      if (result) result.dataset.state = state;
      if (value) value.textContent = text || '';
    }

    setText(selector, text) {
      const element = this.querySelector(selector);
      if (element) element.textContent = text;
    }

    toNumber(value) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : 0;
    }

    formatNumber(value, decimals) {
      return Number(value).toFixed(decimals);
    }
  }

  customElements.define('abok-backup-calculator', AbokBackupCalculator);
})();
